import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Trophy,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  SkipForward,
  ArrowLeft,
  TrendingUp,
  AlertTriangle,
  Loader2,
  Award,
  BarChart3,
  Mail,
  BookOpen,
} from 'lucide-react'
import { useAttemptAnalytics, useTestAttempt } from '../hooks/useMockTest'
import { useAccountType, UpgradeCTA, FREE_TIER_LIMITS } from '@/features/payments'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { trackFeedbackShown, trackFeedbackSubmitted } from '@/shared/utils/analytics'
import FreeTestFeedbackModal from '../components/FreeTestFeedbackModal'

// Helper to format time
const formatTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}

// Helper to format average time per question
const formatAvgTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}

// Helper to get subject display name
const getSubjectDisplayName = (code: string): string => {
  const names: Record<string, string> = {
    PHYSICS: 'Physics',
    CHEMISTRY: 'Chemistry',
    BIOLOGY: 'Biology',
    BOTANY: 'Botany',
    ZOOLOGY: 'Zoology',
  }
  return names[code] || code
}

// Helper to get difficulty color
const getDifficultyColor = (difficulty: string): string => {
  switch (difficulty) {
    case 'EASY':
      return 'text-green-600 bg-green-50'
    case 'MEDIUM':
      return 'text-yellow-600 bg-yellow-50'
    case 'HARD':
      return 'text-red-600 bg-red-50'
    default:
      return 'text-gray-600 bg-gray-50'
  }
}

// Helper to get score color based on percentage
const getScoreColor = (percentage: number): string => {
  if (percentage >= 80) return 'text-green-600'
  if (percentage >= 60) return 'text-blue-600'
  if (percentage >= 40) return 'text-yellow-600'
  return 'text-red-600'
}

export default function TestResultsPage() {
  const { testId, attemptId } = useParams<{ testId: string; attemptId: string }>()
  const navigate = useNavigate()
  const { isFreeUser } = useAccountType()
  const { user } = useAuthStore()
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showNoEmailModal, setShowNoEmailModal] = useState(false)

  const { data: analyticsData, isLoading: isLoadingAnalytics, error: analyticsError } = useAttemptAnalytics(attemptId || '')
  const { data: attemptData, isLoading: isLoadingAttempt } = useTestAttempt(attemptId || '')

  const testCode = attemptData?.data?.testCode || ''

  // Check if this is a free test
  const isFreeTest = testCode && FREE_TIER_LIMITS.allowedTestCodes.includes(testCode)

  // Track ViewContent for FB Pixel
  useEffect(() => {
    if (testCode) {
      fbTrackViewContent('test_results', testCode, `Test Results: ${testCode}`)
    }
  }, [testCode])

  // Show feedback modal for free tests (with delay)
  useEffect(() => {
    if (!testCode || !isFreeTest) return

    // Check if feedback was already submitted or skipped for this test
    const feedbackStatus = localStorage.getItem(`free_test_feedback_${testCode}`)
    if (feedbackStatus) return

    // Show modal after a small delay to let user see their results first
    const timer = setTimeout(() => {
      setShowFeedbackModal(true)
      trackFeedbackShown(testCode)
    }, 2000)

    return () => clearTimeout(timer)
  }, [testCode, isFreeTest])

  // Show no-email modal for phone-only users after results load
  useEffect(() => {
    if (!user || user.email || isLoadingAnalytics || isLoadingAttempt) return
    const timer = setTimeout(() => setShowNoEmailModal(true), 1500)
    return () => clearTimeout(timer)
  }, [user, isLoadingAnalytics, isLoadingAttempt])

  const handleFeedbackSubmitSuccess = () => {
    trackFeedbackSubmitted(testCode)
  }

  const analytics = analyticsData?.data
  const attempt = attemptData?.data

  const handleBackToTests = () => {
    navigate('/test-series')
  }

  // Loading state
  if (isLoadingAnalytics || isLoadingAttempt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (analyticsError || !analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Results</h2>
          <p className="text-gray-600 mb-4">
            {analyticsError instanceof Error ? analyticsError.message : 'Unable to load test results. Please try again.'}
          </p>
          <button
            onClick={handleBackToTests}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Test Series
          </button>
        </div>
      </div>
    )
  }

  const { subjectWiseAnalysis, difficultyWiseAnalysis, timeAnalysis, behavioralInsights } = analytics

  // Safely access overallPerformance with defaults
  const overallPerformance = {
    totalMarks: analytics.overallPerformance?.totalMarks ?? 0,
    maxMarks: analytics.overallPerformance?.maxMarks ?? 0,
    percentage: analytics.overallPerformance?.percentage ?? 0,
    accuracy: analytics.overallPerformance?.accuracy ?? 0,
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={handleBackToTests}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Test Results</h1>
            <p className="text-sm text-gray-500">
              {attempt?.testCode && `Test: ${attempt.testCode}`}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Score Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-4">
              <Trophy className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-1">
              <span className={getScoreColor(overallPerformance.percentage)}>
                {overallPerformance.totalMarks.toFixed(1)}
              </span>
              <span className="text-xl text-gray-500"> / {overallPerformance.maxMarks}</span>
            </h2>
            <p className="text-gray-600">Total Score</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{overallPerformance.percentage.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">Percentage</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{overallPerformance.accuracy.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">Accuracy</div>
            </div>
          </div>
        </div>

        {/* Personalized Report Message */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Your Personalized Report is sent to your Email!
            </h3>
          </div>
          <p className="text-gray-600 text-left">
            An AI-generated personalized report for this test is sent to your registered email address.
            The report includes in-depth analysis of your performance, strengths, areas for improvement, and personalized recommendations.
          </p>
          <p className="text-sm text-gray-500 mt-3 text-left">
            You can also download your report from{' '}
            <span className="font-medium text-gray-700">Menu → Analytics → My Performance</span> page.
          </p>
          <p className="text-sm text-gray-500 mt-2 text-left">
            If you do not receive your report within 1 hour, please write to us at{' '}
            <a
              href="mailto:studentsupport@myneetbuddy.com"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              studentsupport@myneetbuddy.com
            </a>{' '}
            from your registered email address.
          </p>
        </div>

        {/* Subject-wise Analysis */}
        {subjectWiseAnalysis && subjectWiseAnalysis.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Subject-wise Performance</h3>
            </div>

            <div className="space-y-4">
              {subjectWiseAnalysis.map((subject) => (
                <div key={subject.subjectCode} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{getSubjectDisplayName(subject.subjectCode)}</h4>
                    <span className="text-lg font-semibold text-blue-600">{(subject.marks ?? 0).toFixed(1)} marks</span>
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-gray-100 rounded-full mb-3 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{ width: `${subject.accuracy ?? 0}%` }}
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Target className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{subject.totalQuestions} Qs</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">{subject.correct} Correct</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-red-600">{subject.incorrect} Wrong</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <SkipForward className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{subject.skipped} Skipped</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      <span className="text-blue-600">{(subject.accuracy ?? 0).toFixed(1)}% Acc</span>
                    </div>
                  </div>

                  {subject.avgTimePerQuestion > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Avg. time per question: {formatAvgTime(subject.avgTimePerQuestion)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Difficulty-wise Analysis */}
        {difficultyWiseAnalysis && difficultyWiseAnalysis.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Difficulty-wise Performance</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {difficultyWiseAnalysis.map((diff) => (
                <div
                  key={diff.difficulty}
                  className={`rounded-lg p-4 ${getDifficultyColor(diff.difficulty)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium capitalize">{diff.difficulty?.toLowerCase() ?? 'unknown'}</span>
                    <span className="text-lg font-bold">{(diff.accuracy ?? 0).toFixed(1)}%</span>
                  </div>
                  <div className="text-sm opacity-80">
                    <div>{diff.correct} / {diff.totalQuestions} correct</div>
                    <div>{diff.incorrect} wrong, {diff.skipped} skipped</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Analysis */}
        {timeAnalysis && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-gray-900">Time Analysis</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">
                  {formatTime(timeAnalysis.totalTimeTaken)}
                </div>
                <div className="text-xs text-gray-600">Total Time</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-xl font-bold text-blue-600">
                  {formatAvgTime(timeAnalysis.avgTimePerQuestion)}
                </div>
                <div className="text-xs text-gray-600">Avg per Question</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-xl font-bold text-green-600">
                  {timeAnalysis.questionsUnder30Seconds}
                </div>
                <div className="text-xs text-gray-600">Quick (&lt;30s)</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-xl font-bold text-red-600">
                  {timeAnalysis.questionsOver2Minutes}
                </div>
                <div className="text-xs text-gray-600">Slow (&gt;2min)</div>
              </div>
            </div>
          </div>
        )}

        {/* Behavioral Insights */}
        {behavioralInsights && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900">Behavioral Insights</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-700">
                  {behavioralInsights.totalAnswerChanges}
                </div>
                <div className="text-xs text-gray-600">Answer Changes</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-600">
                  {behavioralInsights.questionsMarkedForReview}
                </div>
                <div className="text-xs text-gray-600">Marked for Review</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-lg font-bold text-yellow-600">
                  {behavioralInsights.guessedQuestions}
                </div>
                <div className="text-xs text-gray-600">Possibly Guessed</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-lg font-bold text-orange-600">
                  {behavioralInsights.lowConfidenceQuestions}
                </div>
                <div className="text-xs text-gray-600">Low Confidence</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-lg font-bold text-red-600">
                  {behavioralInsights.hesitationQuestions}
                </div>
                <div className="text-xs text-gray-600">Hesitation</div>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade CTA for Free Users */}
        {isFreeUser && (
          <UpgradeCTA
            location="test_results"
            title="Improve Your Score with PYQs"
            description="Master weak areas by practicing with 20+ years of NEET questions. Step-by-step solutions help you understand concepts deeply."
            buttonText="Unlock All PYQs"
          />
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={() => navigate(`/mock-test/${testId || testCode}/review/${attemptId}`)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            Review Questions
          </button>
          <button
            onClick={handleBackToTests}
            className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Test Series
          </button>
        </div>
      </main>

      {/* Free Test Feedback Modal */}
      <FreeTestFeedbackModal
        testCode={testCode}
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmitSuccess={handleFeedbackSubmitSuccess}
      />

      {/* No Email Modal — shown to phone-only users after submission */}
      {showNoEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Not Sent</h3>
            <p className="text-sm text-gray-600 mb-6">
              We were unable to send your test report since your email address is not available. You can still view your detailed performance in the <strong>My Performance</strong> section.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setShowNoEmailModal(false); navigate('/analytics/my-performance') }}
                className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to My Performance
              </button>
              <button
                onClick={() => setShowNoEmailModal(false)}
                className="w-full px-4 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
